import * as React from 'react';
import { StyleSheet, View, Image, Text } from 'react-native';
import { useHistory } from 'react-router';
import { useApolloClient } from '@apollo/react-hooks';

import { MerchantOffer } from '../../../api/graphql/fragments/offers';
import color from '../../../style/color';
import { font } from '../../../style/text';
import { HOME_SCREEN_WIDTH } from '../../../style/size';
import { containerStyles } from '../../../style/container';
import { useMerchantOffer } from '../../../lib/offers/offers';
import { AffiliateLinkLoadingTriggeringEventType } from '../../../lib/offers/affiliateLinkLoading';
import { logAmplitudeEvent } from '../../../lib/events/amplitudeEvents';
import { logUserEventUtil } from '../../../lib/events/userEvents';
import OfferButton from './OfferButton';
import FavoriteOfferButton from './FavoriteOfferButton';
import OfferCashback from './OfferCashback';
import ExpirationDate from './ExpirationDate';
import { HoverableTouchableOpacity } from '../../common/HoverableComponents';

export default function SmallOfferComponent({ offerId }: { offerId: string }) {
    const history = useHistory();
    const apolloClient = useApolloClient();
    const offer: MerchantOffer | undefined = useMerchantOffer(offerId);
    return (
        <HoverableTouchableOpacity
            style={styles.container}
            hoveredStyle={styles.containerHovered}
            onPress={() => {
                if (offer) {
                    logAmplitudeEvent({ name: 'Offers - Clicked Offer', properties: { offerId } });
                    logUserEventUtil(apolloClient, {
                        type: 'clickedOffer',
                        payload: { offerId, type: offer.type, from: 'webApp' },
                    });
                    history.push(`/home/offer/${offer.offerId}`);
                }
            }}>
            <OfferInfo {...{ offer }} />
            <OfferButton {...{ offer }} triggeringEventType={AffiliateLinkLoadingTriggeringEventType.offerComponent} />
        </HoverableTouchableOpacity>
    );
}

export function OfferInfo({ offer }: { offer: MerchantOffer | undefined }) {
    const bannerURL = offer?.card?.bannerURL ?? offer?.screen?.bannerURL;
    const title = offer?.card?.title ?? offer?.screen?.title;
    return (
        <View style={containerStyles.containerRowLeftCenter}>
            <View>
                {bannerURL ? <Image source={{ uri: bannerURL }} style={styles.image} /> : <View style={styles.image} />}
                <FavoriteOfferButton
                    {...{ offer }}
                    style={styles.containerFavoriteOfferButton}
                    iconStyle={styles.imageFavoriteOfferButton}
                />
            </View>
            <View style={styles.containerTexts}>
                {offer && title ? (
                    <>
                        <Text style={styles.textTitle} numberOfLines={1}>
                            {title}
                        </Text>
                        <OfferCashback {...{ offer }} />
                    </>
                ) : (
                    <>
                        <View style={[styles.containerPlaceholder, { width: 60 }]} />
                        <View style={[styles.containerPlaceholder, { width: 120 }]} />
                    </>
                )}
            </View>
            <View style={styles.containerExpirationDate}>{offer ? <ExpirationDate offer={offer} /> : null}</View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: Math.floor((HOME_SCREEN_WIDTH - 20) / 2),
    },
    containerFavoriteOfferButton: {
        position: 'absolute',
        right: 5,
        top: 5,
    },
    containerHovered: {
        opacity: 0.8,
    },
    containerTexts: {
        flex: 1,
        marginLeft: 15,
        marginRight: 10,
    },
    containerExpirationDate: {
        position: 'absolute',
        bottom: 4,
        left: 4,
    },
    containerPlaceholder: {
        height: 14,
        marginVertical: 3,
        borderRadius: 5,
        backgroundColor: color.linkWaterLight,
    },
    image: {
        width: 108,
        height: 72,
        borderRadius: 8,
        backgroundColor: color.linkWaterLight,
    },
    imageFavoriteOfferButton: {
        width: 18,
        height: 15,
    },
    textTitle: {
        fontFamily: font.ambitBlack,
        fontSize: 14,
        color: color.black,
    },
});
